import { useMemo, useState } from 'react';
import { useTranslations } from '@/i18n/use-translations.tsx';
import { useEventStore } from '@/state/stores/use-event-store.ts';
import { useNotifications } from '@/hooks/use-notifications';
import useCreateContributorMutation from '@/state/mutations/contributors/use-create-contributor.ts';
import { ContributorAutocomplete, type ActorOption } from '../../common/contributor-autocomplete';
import type { FlowDto } from '@/types/dtos/flows/response/flow.dto';
import useAddFlowContributor from '@/state/mutations/flows/add-flow-contributor';
import { getAvailableFlowContributors } from '@/utils/contributor-extraction.utils';
import { useQueryClient } from '@tanstack/react-query';
import { GET_FLOW_QUERY_KEY } from '@/state/queries/flows/use-get-flow';
import { settleContributorAddVisibility } from '@/helpers/settle-contributor-add-visibility.helper';
import { getObjectId } from '@/helpers/object.id.helper';

export const AddFlowModeratorInput = ({
  flow,
  onSuccess,
}: {
  flow: FlowDto;
  eventSlotId?: string;
  onSuccess: () => void;
}) => {
  const t = useTranslations();
  const notification = useNotifications();
  const contributors = useEventStore((s) => s.contributors);
  const queryClient = useQueryClient();
  const { mutateAsync: addFlowContributorMutation, isPending: isAddContributorPending } = useAddFlowContributor();
  const { mutateAsync: createContributorMutation, isPending: isCreateContributorPending } =
    useCreateContributorMutation();
  const [isSettling, setIsSettling] = useState(false);

  const eventId = useMemo(() => {
    if (!flow.parent) return undefined;
    if (typeof flow.parent === 'string') return flow.parent;
    if (typeof flow.parent === 'object' && 'id' in flow.parent) return flow.parent.id;
    return undefined;
  }, [flow.parent]);

  const actors = useMemo<ActorOption[]>(() => {
    if (!contributors?.length) return [];
    const availableContributors = getAvailableFlowContributors(flow, contributors);
    return availableContributors.map((contributor) => ({
      id: contributor.id,
      name: contributor.profiles[0]?.displayName || `Contributor ${contributor.id.slice(-6)}`,
    }));
  }, [contributors, flow]);

  const settleAfterAdd = async () => {
    const flowKeys: (readonly unknown[])[] = [[GET_FLOW_QUERY_KEY, flow.id]];
    const flowOriginId = flow.origin ? getObjectId(flow.origin) : undefined;
    if (flowOriginId && flowOriginId !== flow.id) {
      flowKeys.push([GET_FLOW_QUERY_KEY, flowOriginId]);
    }
    await settleContributorAddVisibility(queryClient, {
      eventId,
      extraQueryKeys: flowKeys,
    });
  };

  const addAsHost = async (contributorId: string, name: string) => {
    // addFlowContributor binds the moderator role to the origin flow, creates slots,
    // and appends the host playlist ref (create-with-roles alone does not).
    await addFlowContributorMutation({
      flowId: flow.id,
      flowVersion: flow.version,
      contributorId,
    });
    notification.notifySuccess(t('flows.notifications.addContributorSuccess', { name }));
    await settleAfterAdd();
    onSuccess();
  };

  const addSessionContributor = async (actor: ActorOption) => {
    if (!contributors) return;
    setIsSettling(true);
    try {
      await addAsHost(actor.id, actor.name);
    } catch (error) {
      console.error(error);
      notification.notifyError(t('flows.notifications.addContributorError'));
    } finally {
      setIsSettling(false);
    }
  };

  const createContributor = async (name: string) => {
    setIsSettling(true);
    try {
      if (!eventId) {
        throw new Error('Cannot create contributor: event ID not found in flow.parent');
      }

      const newContributor = await createContributorMutation({
        event: eventId,
        profiles: [{ displayName: name, editor: 'Event' }],
        roles: [],
      });

      await addAsHost(newContributor.id, name);
    } catch (error) {
      console.error(error);
      notification.notifyError(t('flows.notifications.addContributorError'));
    } finally {
      setIsSettling(false);
    }
  };

  const isPending = isAddContributorPending || isCreateContributorPending || isSettling;

  return (
    <ContributorAutocomplete
      options={actors}
      loading={isPending}
      labelKey="flows.addModeratorLabel"
      autocompleteId="add-flow-moderator-autocomplete"
      disabled={isPending}
      onSelectExisting={addSessionContributor}
      onCreateNew={createContributor}
    />
  );
};
